import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../admin/api";
import { Inbox, Mail, MailOpen, FileDown, Wallet, MapPin, ArrowRight } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState({ total: 0, active: 0, archived: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [all, active, archived, recentRes] = await Promise.all([
          api.get("/admin/submissions?limit=1"),
          api.get("/admin/submissions?archived=false&limit=1"),
          api.get("/admin/submissions?archived=true&limit=1"),
          api.get("/admin/submissions?limit=5"),
        ]);
        if (!mounted) return;
        setStats({
          total: all.data.total,
          active: active.data.total,
          archived: archived.data.total,
        });
        setRecent(recentRes.data.items || []);
      } catch (e) {
        // ignore — protected route will handle 401
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const cards = [
    { label: "Anfragen gesamt", value: stats.total, icon: Inbox, color: "#1d428a" },
    { label: "Offen", value: stats.active, icon: Mail, color: "#df4661" },
    { label: "Archiviert", value: stats.archived, icon: MailOpen, color: "#5A6270" },
  ];

  const quickLinks = [
    { to: "/admin/inquiries", label: "Anfragen verwalten", icon: Inbox },
    { to: "/admin/models", label: "Servicemodelle bearbeiten", icon: Wallet },
    { to: "/admin/contact-info", label: "Kontaktdaten ändern", icon: MapPin },
  ];

  return (
    <div data-testid="admin-dashboard" className="p-6 md:p-10 max-w-6xl">
      <div className="font-mono-tech text-[#1d428a] mb-2">
        <span className="inline-block w-2 h-2 bg-[#df4661] mr-3 align-middle" />
        ADMIN · ÜBERSICHT
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#0A0A0A]">Willkommen.</h1>
      <p className="mt-3 text-[#5A6270] text-sm md:text-base">
        Verwalten Sie Kontaktanfragen, Servicemodelle und Kontaktdaten der Webseite.
      </p>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#1d428a]/15 border border-[#1d428a]/15 mt-10">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} className="bg-white p-6">
              <div className="flex items-start justify-between">
                <div className="font-mono-tech text-[#5A6270]">{c.label.toUpperCase()}</div>
                <Icon size={20} strokeWidth={1.6} style={{ color: c.color }} />
              </div>
              <div className="font-display text-5xl md:text-6xl mt-3" style={{ color: c.color }}>
                {c.value}
              </div>
            </div>
          );
        })}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#1d428a]/15 border border-[#1d428a]/15 mt-10">
        {quickLinks.map((q, i) => {
          const Icon = q.icon;
          return (
            <Link
              key={i}
              to={q.to}
              className="bg-white p-6 hover:bg-[#1d428a] hover:text-white group transition-colors"
            >
              <Icon size={22} strokeWidth={1.4} className="text-[#1d428a] group-hover:text-[#df4661] transition-colors mb-6" />
              <div className="font-display-mid text-xl">{q.label}</div>
              <div className="mt-4 flex items-center gap-2 font-mono-tech text-[#df4661]">
                ÖFFNEN <ArrowRight size={14} strokeWidth={2} />
              </div>
            </Link>
          );
        })}
      </section>

      <section className="mt-10">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-2xl md:text-3xl text-[#1d428a]">Letzte Anfragen</h2>
          <Link to="/admin/inquiries" className="font-mono-tech text-[#df4661] hover:text-[#1d428a]">
            ALLE ANZEIGEN →
          </Link>
        </div>
        <div className="border border-[#1d428a]/15 bg-white divide-y divide-[#1d428a]/15">
          {recent.length === 0 && (
            <div className="p-5 text-[#5A6270] text-sm">Noch keine Anfragen vorhanden.</div>
          )}
          {recent.map((s) => (
            <Link
              key={s.id}
              to={`/admin/inquiries?focus=${s.id}`}
              className="grid grid-cols-12 gap-3 p-4 hover:bg-[#e8ecf3] transition-colors"
            >
              <div className="col-span-12 md:col-span-3 font-mono-tech text-[#5A6270]">
                {new Date(s.created_at).toLocaleString("de-CH")}
              </div>
              <div className="col-span-12 md:col-span-3 font-display-mid text-[#0A0A0A]">{s.name}</div>
              <div className="col-span-12 md:col-span-3 text-sm text-[#5A6270] truncate">{s.email}</div>
              <div className="col-span-12 md:col-span-3 font-mono-tech text-[#1d428a]">{s.project_type}</div>
            </Link>
          ))}
        </div>
      </section>

      <a
        href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions.csv`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="admin-export-csv"
        className="mt-10 inline-flex items-center gap-3 border border-[#1d428a] text-[#1d428a] font-mono-tech px-5 py-3 hover:bg-[#1d428a] hover:text-white transition-colors"
      >
        <FileDown size={15} strokeWidth={1.8} />
        ALLE ANFRAGEN ALS CSV
      </a>
    </div>
  );
}
